import { isDefined, makeValuesPredicate } from '@sgme/fp';
import { isBefore } from 'date-fns';

import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { BlotterFilterPresetRange } from '@/store/blotter/daterangeCustomPresets.ts';
import type { BlotterFilter } from '@/store/blotter/types/index.ts';
import type { ISODateTime } from '@/types/date.ts';

// all these presets don't include today's notifications
const isPresetNotToday = makeValuesPredicate<BlotterFilterPresetRange>(['yesterday', 'lastWeek', 'lastMonth']);

/**
 * Tells whether blotter live notifications should not be displayed in the blotter
 * For instance no live notifications should be displayed when the date filter is set to the 'last month' preset
 * please note that this is not related to grid filters
 *    for these filters we have to accept all incoming trades and let grid filters on the list
 * any change to the external filters will trigger a call to the services in order to update the whole trade list
 *   grid filters act over this list
 * @param tradeUpdateTime
 * @param filter The current blotter date filter value
 */
export function mustDiscardLiveNotifications(
  tradeUpdateTime: ISODateTime,
  filter: BlotterFilter,
  orderId?: string,
): boolean {
  if (filter.type === 'intraday') {
    return false;
  }
  if (filter.subtype === 'preset') {
    logger.logInformation(
      `[ORDER] mustDiscardLiveNotifications: preset filter => discard live notifications ${orderId}`,
    );
    return isPresetNotToday(filter.preset);
  }
  // custom range
  // we don't consider functional impossibilities here (can't query historical trades unless both range boundaries are set)
  // we simply answer the question asked the function
  const { fromDate, toDate } = filter.range;
  return (
    // range infinite in both direction
    // trade updated before the range starts
    (isDefined(fromDate) && isBefore(tradeUpdateTime, fromDate)) ||
    // trade updated after the range stops
    (isDefined(toDate) && isBefore(toDate, tradeUpdateTime))
  );
}
